<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useCurrency } from 'dashboard/composables/useCurrency';
import DealReportsAPI from 'dashboard/api/dealReports';
import Button from 'dashboard/components-next/button/Button.vue';
import SummaryCards from './components/SummaryCards.vue';
import ConversionFunnel from './components/ConversionFunnel.vue';
import LossReasonsChart from './components/LossReasonsChart.vue';
import AgentsPerformance from './components/AgentsPerformance.vue';
import DealsTimeline from './components/DealsTimeline.vue';
import ForecastWidget from './components/ForecastWidget.vue';
import VelocityChart from './components/VelocityChart.vue';
import AgingOverview from './components/AgingOverview.vue';
import GoalProgress from './components/GoalProgress.vue';

const store = useStore();
const { t, locale } = useI18n();
const browserLocale = computed(() => locale.value.replace('_', '-'));
const { currency } = useCurrency();

// State
const isLoading = ref(false);
const selectedFunnelId = ref(null);
const selectedPeriod = ref('30d');
const customStartDate = ref('');
const customEndDate = ref('');

// Report data
const summaryData = ref(null);
// Funil de conversão vem do livro de eventos (conversion_funnel), não da
// foto da etapa atual (by_stage) — antes o cálculo pelo ledger existia e
// nunca era exibido.
const conversionData = ref(null);
const agentData = ref(null);
const lossReasonsData = ref(null);
const timelineData = ref(null);
const forecastData = ref(null);
const velocityData = ref(null);
const agingData = ref(null);

// Computed
const funnels = computed(() => store.getters['funnels/getFunnels'] || []);

const periodOptions = computed(() => [
  { value: '7d', label: t('DEAL_REPORTS.FILTERS.PERIOD_7D') },
  { value: '30d', label: t('DEAL_REPORTS.FILTERS.PERIOD_30D') },
  { value: '90d', label: t('DEAL_REPORTS.FILTERS.PERIOD_90D') },
  { value: '365d', label: t('DEAL_REPORTS.FILTERS.PERIOD_1Y') },
  { value: 'custom', label: t('DEAL_REPORTS.FILTERS.PERIOD_CUSTOM') },
]);

const dateRange = computed(() => {
  const today = new Date();
  let startDate;
  let endDate = today;

  switch (selectedPeriod.value) {
    case '7d':
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 7);
      break;
    case '30d':
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 30);
      break;
    case '90d':
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 90);
      break;
    case '365d':
      startDate = new Date(today);
      startDate.setFullYear(today.getFullYear() - 1);
      break;
    case 'custom':
      startDate = customStartDate.value
        ? new Date(customStartDate.value)
        : new Date(today.setDate(today.getDate() - 30));
      endDate = customEndDate.value
        ? new Date(customEndDate.value)
        : new Date();
      break;
    default:
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 30);
  }

  return {
    start_date: startDate.toISOString().split('T')[0],
    end_date: endDate.toISOString().split('T')[0],
  };
});

const showCustomDatePicker = computed(() => selectedPeriod.value === 'custom');

// Methods
const fetchReports = async () => {
  isLoading.value = true;

  try {
    const params = {
      ...dateRange.value,
      funnel_id: selectedFunnelId.value,
    };

    if (!selectedFunnelId.value) return;

    const [
      summary,
      conversionFunnel,
      byAgent,
      lossReasons,
      timeline,
      forecast,
      velocity,
      aging,
    ] = await Promise.all([
      DealReportsAPI.getSummary(params),
      DealReportsAPI.getConversionFunnel(params),
      DealReportsAPI.getByAgent(params),
      DealReportsAPI.getLossReasons(params),
      DealReportsAPI.getTimeline(params),
      DealReportsAPI.getForecast(params),
      DealReportsAPI.getVelocity(params),
      DealReportsAPI.getAging(params),
    ]);

    summaryData.value = summary.data;
    conversionData.value = conversionFunnel.data;
    agentData.value = byAgent.data;
    lossReasonsData.value = lossReasons.data;
    timelineData.value = timeline.data;
    forecastData.value = forecast.data;
    velocityData.value = velocity.data;
    agingData.value = aging.data;
  } catch {
    summaryData.value = null;
  } finally {
    isLoading.value = false;
  }
};

const formatCurrency = value => {
  return new Intl.NumberFormat(browserLocale.value, {
    style: 'currency',
    currency: currency.value,
  }).format(value || 0);
};

// Watch for filter changes
watch(
  [selectedFunnelId, selectedPeriod, customStartDate, customEndDate],
  () => {
    if (
      selectedPeriod.value !== 'custom' ||
      (customStartDate.value && customEndDate.value)
    ) {
      fetchReports();
    }
  }
);

// Load data on mount
onMounted(async () => {
  await store.dispatch('funnels/get');
  if (funnels.value.length > 0) {
    selectedFunnelId.value = funnels.value[0].id;
  }
  fetchReports();
});
</script>

<template>
  <div
    class="deal-reports-dashboard p-6 bg-slate-50 dark:bg-slate-900 min-h-full"
  >
    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-slate-900 dark:text-white mb-2">
        {{ t('DEAL_REPORTS.TITLE') }}
      </h1>
      <p class="text-slate-600 dark:text-slate-400">
        {{ t('DEAL_REPORTS.DESCRIPTION') }}
      </p>
    </div>

    <!-- Filters -->
    <div
      class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-4 mb-6"
    >
      <div class="flex flex-wrap items-center gap-4">
        <!-- Funnel Selector -->
        <div class="flex-1 min-w-48">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ t('DEAL_REPORTS.FILTERS.FUNNEL') }}
          </label>
          <select
            v-model="selectedFunnelId"
            class="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          >
            <option
              v-for="funnel in funnels"
              :key="funnel.id"
              :value="funnel.id"
            >
              {{ funnel.name }}
            </option>
          </select>
        </div>

        <!-- Period Selector -->
        <div class="flex-1 min-w-48">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ t('DEAL_REPORTS.FILTERS.PERIOD') }}
          </label>
          <select
            v-model="selectedPeriod"
            class="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          >
            <option
              v-for="option in periodOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- Custom Date Range -->
        <template v-if="showCustomDatePicker">
          <div class="flex-1 min-w-40">
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ t('DEAL_REPORTS.FILTERS.START_DATE') }}
            </label>
            <input
              v-model="customStartDate"
              type="date"
              class="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div class="flex-1 min-w-40">
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ t('DEAL_REPORTS.FILTERS.END_DATE') }}
            </label>
            <input
              v-model="customEndDate"
              type="date"
              class="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </template>

        <!-- Refresh Button -->
        <div class="flex items-end">
          <Button
            icon="i-lucide-refresh-cw"
            :is-loading="isLoading"
            slate
            faded
            @click="fetchReports"
          >
            {{ t('DEAL_REPORTS.REFRESH') }}
          </Button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div
      v-if="isLoading && !summaryData"
      class="flex items-center justify-center py-20"
    >
      <div class="text-center">
        <div
          class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"
        />
        <p class="text-slate-500">{{ t('DEAL_REPORTS.LOADING') }}</p>
      </div>
    </div>

    <!-- Dashboard Content -->
    <div v-else class="space-y-6">
      <!-- Summary Cards -->
      <SummaryCards
        v-if="summaryData"
        :data="summaryData"
        :format-currency="formatCurrency"
      />

      <!-- Charts Row 1 -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Won vs Lost Timeline -->
        <DealsTimeline
          v-if="timelineData"
          :data="timelineData"
          :format-currency="formatCurrency"
        />

        <!-- Conversion Funnel -->
        <ConversionFunnel v-if="conversionData" :data="conversionData" />
      </div>

      <!-- Charts Row 2 -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Loss Reasons -->
        <LossReasonsChart v-if="lossReasonsData" :data="lossReasonsData" />

        <!-- Agents Performance -->
        <AgentsPerformance
          v-if="agentData"
          :data="agentData"
          :format-currency="formatCurrency"
        />
      </div>

      <!-- Pipeline Metrics Row -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Pipeline Forecast -->
        <ForecastWidget
          v-if="forecastData"
          :data="forecastData"
          :format-currency="formatCurrency"
        />

        <!-- Pipeline Velocity -->
        <VelocityChart v-if="velocityData" :data="velocityData" />
      </div>

      <!-- Aging & Goals Row -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Deal Aging -->
        <AgingOverview v-if="agingData" :data="agingData" />

        <!-- Sales Goals -->
        <GoalProgress :funnel-id="selectedFunnelId" />
      </div>
    </div>
  </div>
</template>
